import { usePaywallStates } from '@/atoms/paywallAtoms';
import useReverseTrial from '@/hooks/useReverseTrial';
import { trackButtonClicked } from '@/lib/analytics/scribe/button';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { Button } from '@heidi/design-system/button';
import { TypographyH5 } from '@heidi/design-system/typography';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { useTranslations } from '@heidi/runtime/i18n';

interface PracticeTrialUpgradeBannerProps {
  onBeforeUpgrade?: () => void;
}

export function PracticeTrialUpgradeBanner({
  onBeforeUpgrade,
}: PracticeTrialUpgradeBannerProps) {
  const { togglePracticeUpgradeModal } = usePaywallStates();
  const { trialDaysLeft } = useReverseTrial();
  const { t } = useTranslations();

  if (isAppleCompliantBuild()) {
    return null;
  }

  const handleUpgrade = () => {
    trackButtonClicked({
      screenName: ScreenName.TrialExtensionModal,
      action: ButtonAction.TrialExtensionUpgradeToPractice,
      label: 'Upgrade to Practice',
    });
    onBeforeUpgrade?.();
    togglePracticeUpgradeModal(true);
  };

  return (
    <div className="relative flex items-center justify-between overflow-hidden bg-tier-practice px-8 py-6">
      <div className="relative z-10 flex flex-col gap-0.5 text-text-primary-on-dark [.skiesTierDark_&]:text-text-primary-invert">
        <span className="font-exposure text-5xl font-semibold leading-none tracking-tight">
          {trialDaysLeft}
        </span>
        <span className="text-sm font-medium">{t('trialExtension.daysLeft')}</span>
      </div>

      {!isAppleCompliantBuild() && (
        <Button
          size="lg"
          variant="ghost"
          onClick={handleUpgrade}
          className="bg-background-primary px-4 py-3 text-text-primary-invert hover:bg-background-primary/100"
        >
          <TypographyH5>{t('trialExtension.upgradeNow')}</TypographyH5>
        </Button>
      )}
    </div>
  );
}
